import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "dialog",
    "closeButton",
    "image",
    "counter",
    "previousButton",
    "nextButton"
  ]

  connect() {
    this.currentIndex = 0
    this.images = this.collectImages()
    this.previousActiveElement = null
    this.openTrigger = null
    this.boundResetForTurboCache = this.resetForTurboCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundResetForTurboCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundResetForTurboCache)
    this.resetForTurboCache()
  }

  open(event) {
    if (!this.hasModalTarget || !this.hasImageTarget) return

    this.images = this.collectImages()
    if (this.images.length === 0) return

    event.preventDefault()
    this.openTrigger = event.currentTarget
    this.previousActiveElement = document.activeElement
    this.currentIndex = this.readIndex(event.currentTarget)

    this.renderCurrentImage()
    this.showModal()
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.modalOpen()) return

    this.modalTarget.classList.add("hidden")
    this.modalTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("ec-image-viewer-open")
    this.restoreFocus()
  }

  resetForTurboCache() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.setAttribute("aria-hidden", "true")
    }

    document.body.classList.remove("ec-image-viewer-open")
    this.openTrigger = null
    this.previousActiveElement = null
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  previous(event) {
    if (event) event.preventDefault()
    if (this.images.length <= 1) return

    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.renderCurrentImage()
  }

  next(event) {
    if (event) event.preventDefault()
    if (this.images.length <= 1) return

    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.renderCurrentImage()
  }

  handleKeydown(event) {
    if (!this.modalOpen()) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
      return
    }

    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
      return
    }

    if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  modalOpen() {
    return this.hasModalTarget && !this.modalTarget.classList.contains("hidden")
  }

  showModal() {
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("ec-image-viewer-open")
    this.focusCloseButton()
  }

  readIndex(trigger) {
    const rawIndex = Number.parseInt(trigger.dataset.imageViewerIndex, 10)

    if (Number.isNaN(rawIndex) || rawIndex < 0) return 0

    return Math.min(rawIndex, this.images.length - 1)
  }

  renderCurrentImage() {
    const imageData = this.images[this.currentIndex]
    if (!imageData) return

    this.imageTarget.src = imageData.src
    this.imageTarget.alt = imageData.alt || ""

    this.updateCounter()
    this.updateNavigation()
  }

  updateCounter() {
    if (!this.hasCounterTarget) return

    this.counterTarget.textContent = `${this.currentIndex + 1} of ${this.images.length}`
  }

  updateNavigation() {
    const multipleImages = this.images.length > 1
    const navButtons = []

    if (this.hasPreviousButtonTarget) navButtons.push(this.previousButtonTarget)
    if (this.hasNextButtonTarget) navButtons.push(this.nextButtonTarget)

    navButtons.forEach((button) => {
      button.disabled = !multipleImages
      button.classList.toggle("hidden", !multipleImages)
      button.setAttribute("aria-hidden", (!multipleImages).toString())
    })
  }

  trapFocus(event) {
    const focusable = this.focusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusCloseButton() {
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.focus()
      return
    }

    const firstFocusable = this.focusableElements()[0]
    if (firstFocusable) firstFocusable.focus()
  }

  focusableElements() {
    const container = this.hasDialogTarget ? this.dialogTarget : this.modalTarget

    return Array.from(
      container.querySelectorAll(
        "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
      )
    )
  }

  collectImages() {
    return Array.from(this.element.querySelectorAll("[data-image-viewer-src]")).filter((trigger) => {
      return trigger.dataset.imageViewerSrc
    }).map((trigger) => {
      return {
        src: trigger.dataset.imageViewerSrc,
        alt: trigger.dataset.imageViewerAlt || ""
      }
    })
  }

  restoreFocus() {
    const focusTarget = this.openTrigger || this.previousActiveElement

    if (focusTarget && document.contains(focusTarget)) {
      focusTarget.focus()
    }

    this.openTrigger = null
  }
}
